import { useMemo } from "react";
import { ChevronDown, X } from "lucide-react";
import { useExplore } from "@/lib/ExploreContext";
import { subcategoriesFor, catMatches } from "@/data/taxonomy";
import { DISPLAY_ORDER, MORE_EXPERIENCES, bucketFor } from "@/data/experiences";
import { categoryLabel } from "@/lib/locales";
import { useLang } from "@/lib/i18n";

/**
 * Only two quick questions for every browse category: What and Where.
 * The existing advanced filter sheet holds all less-common combinations.
 * The Experiences options are curated editorial themes, not the provider's
 * inconsistent raw "Service" / "Boat Tour" taxonomy.
 */
export default function CategoryQuickFilters({ category }) {
  const { lang } = useLang();
  const es = lang === "es";
  const {
    allListings, tourExperiences, neighborhoods,
    filterSubs, setFilterSubs, filterNb, setFilterNb,
    experienceBucket, setExperienceBucket, setFilterOpen,
  } = useExplore();
  const themes = useMemo(() => {
    if (category !== "Experiences") return [];
    const available = new Set(
      [...allListings.filter((item) => catMatches(item, "Experiences")), ...tourExperiences]
        .map(bucketFor)
    );
    return DISPLAY_ORDER.filter((label) => label !== MORE_EXPERIENCES && available.has(label));
  }, [category, allListings, tourExperiences]);
  const choices = category === "Experiences" ? themes : subcategoriesFor(category);
  const selectedType = category === "Experiences"
    ? experienceBucket
    : filterSubs.size === 1 ? [...filterSubs][0] : "";
  const chosenArea = [...filterNb][0] || "";
  const active = (category === "Experiences" && experienceBucket !== "All")
    || filterSubs.size > 0 || filterNb.size > 0;

  const field = "relative flex min-w-0 flex-1 items-center rounded-[2px] border border-[#d6d3cd] bg-white text-[#171717] focus-within:border-[#171717] focus-within:ring-1 focus-within:ring-[#171717] dark:border-white/25 dark:bg-[#242326] dark:text-white";
  const select = "h-11 w-full min-w-0 appearance-none bg-transparent py-1 pl-3 pr-8 text-[12px] font-semibold outline-none sm:text-[13px]";

  const clear = () => {
    setFilterSubs(new Set());
    setFilterNb(new Set());
    setExperienceBucket("All");
  };

  return (
    <div className="border-t border-black/[0.055] bg-[#faf9f7] dark:border-white/[0.09] dark:bg-[#1d1c1f]">
      <div className="mx-auto flex max-w-[1240px] items-center gap-2 px-3 py-2 sm:gap-3 sm:px-6 lg:px-8">
        <div className="hidden shrink-0 text-[10px] font-bold uppercase tracking-[.10em] text-[#77736d] dark:text-white/55 md:block">
          {es ? "Busca a tu manera" : "Find your thing"}
        </div>
        <label className={field}>
          <span className="sr-only">{es ? "Qué tipo de lugar o actividad" : "Type of place or activity"}</span>
          <select
            value={selectedType === "All" ? "" : selectedType}
            onChange={(event) => {
              const value = event.target.value;
              if (category === "Experiences") {
                setExperienceBucket(value || "All");
              } else {
                setFilterSubs(value ? new Set([value]) : new Set());
              }
            }}
            className={select}
          >
            <option value="">{es ? "Todos los tipos" : "All types"}</option>
            {choices.map((name) => (
              <option key={name} value={name}>{categoryLabel(name, lang)}</option>
            ))}
            {category === "Experiences" && choices.length === 0 && (
              <option disabled value="pending">{es ? "Cargando experiencias…" : "Loading experiences…"}</option>
            )}
          </select>
          <ChevronDown aria-hidden="true" size={15} className="pointer-events-none absolute right-2.5 text-[#777] dark:text-white/60"/>
        </label>
        <label className={field}>
          <span className="sr-only">{es ? "Barrio o municipio" : "Neighborhood or municipality"}</span>
          <select
            className={select}
            value={chosenArea}
            onChange={(event) => setFilterNb(event.target.value ? new Set([event.target.value]) : new Set())}
          >
            <option value="">{es ? "Toda la isla" : "All areas"}</option>
            {neighborhoods.map((area) => (
              <option key={area.id || area.slug} value={area.slug || area.id}>{area.name}</option>
            ))}
          </select>
          <ChevronDown aria-hidden="true" size={15} className="pointer-events-none absolute right-2.5 text-[#777] dark:text-white/60"/>
        </label>
        {active && (
          <button type="button" onClick={clear}
            className="flex h-10 shrink-0 items-center gap-1 px-1 text-[11px] font-semibold text-[#77736d] transition hover:text-[#171717] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E30613] dark:text-white/70 dark:hover:text-white"
            aria-label={es ? "Limpiar los filtros rápidos" : "Clear quick filters"}>
            <X size={15}/><span className="hidden sm:inline">{es ? "Limpiar" : "Clear"}</span>
          </button>
        )}
      </div>
    </div>
  );
}
